export interface ProxyAuthTokenResponse {
  token: string;
  expiresAt?: number | null;
  recipientKey?: string;
  recoveryKey?: string;
}

export type StoredProxyAuthHeaderOptions = {
  chain?: EvmNetworkKey;
  wallet?: string;
};

export function proxyAuthNetworkScope(): EvmNetworkKey {
  const key = EVM_CONFIG.NETWORK?.registryKey;
  if (!key || key === "base" || key === "base-sepolia") return "base";
  if (key === "dogeos-chikyu") return key;
  throw new Error("Unsupported EVM authentication network");
}

function authPrefixes(chain: EvmNetworkKey) {
  const scope = chain === "base-sepolia" ? "base" : chain;
  return {
    token: `stealthos_proxy_auth_token:${scope}:`,
    expiry: `stealthos_proxy_auth_token_exp:${scope}:`,
  };
}

function normalizeWalletKey(wallet?: string): string {
  return String(wallet || "")
    .trim()
    .toLowerCase();
}

export function readStoredProxyAuthToken(
  tokenKey: string,
  expKey: string,
): string {
  if (typeof window === "undefined") {
    return "";
  }

  const existingToken = window.sessionStorage.getItem(tokenKey) || "";
  const expRaw = window.sessionStorage.getItem(expKey) || "";
  const existingExp = Number(expRaw);
  const now = Math.floor(Date.now() / 1000);
  const hasFiniteExp =
    expRaw.trim() !== "" && Number.isFinite(existingExp) && existingExp > 0;
  if (existingToken && (!hasFiniteExp || existingExp - now > 30)) {
    return existingToken;
  }
  return "";
}

export function readStoredBaseProxyAuthToken(wallet?: string): string {
  return readStoredEvmProxyAuthToken("base", wallet);
}

function readStoredEvmProxyAuthToken(
  chain: EvmNetworkKey,
  wallet?: string,
): string {
  const prefixes = authPrefixes(chain);
  const normalizedWallet = normalizeWalletKey(wallet);
  if (!normalizedWallet) {
    return "";
  }
  const legacyToken = readStoredProxyAuthToken(
    `${prefixes.token}${normalizedWallet}`,
    `${prefixes.expiry}${normalizedWallet}`,
  );
  if (legacyToken || typeof window === "undefined") {
    return legacyToken;
  }

  const scopedPrefix = `${prefixes.token}${normalizedWallet}:`;
  const scopedExpPrefix = `${prefixes.expiry}${normalizedWallet}:`;
  const candidates: Array<{ token: string; exp: number }> = [];
  for (let i = 0; i < window.sessionStorage.length; i += 1) {
    const key = window.sessionStorage.key(i) || "";
    if (!key.startsWith(scopedPrefix)) continue;
    const suffix = key.slice(scopedPrefix.length);
    const candidate = readStoredProxyAuthCandidate(
      key,
      `${scopedExpPrefix}${suffix}`,
    );
    if (candidate) candidates.push(candidate);
  }
  candidates.sort((a, b) => b.exp - a.exp);
  return candidates[0]?.token || "";
}

function readStoredProxyAuthCandidate(
  tokenKey: string,
  expKey: string,
): { token: string; exp: number } | null {
  if (typeof window === "undefined") {
    return null;
  }

  const token = readStoredProxyAuthToken(tokenKey, expKey);
  if (!token) {
    return null;
  }

  const expRaw = window.sessionStorage.getItem(expKey) || "";
  const exp = Number(expRaw);
  return {
    token,
    exp: Number.isFinite(exp) && exp > 0 ? exp : Number.MAX_SAFE_INTEGER,
  };
}

export function readAnyStoredProxyAuthToken(
  chain: EvmNetworkKey = proxyAuthNetworkScope(),
): string {
  if (typeof window === "undefined") {
    return "";
  }

  const candidates: Array<{ token: string; exp: number }> = [];
  const prefixes = authPrefixes(chain);

  for (let i = 0; i < window.sessionStorage.length; i += 1) {
    const key = window.sessionStorage.key(i) || "";
    if (!key.startsWith(prefixes.token)) {
      continue;
    }
    const suffix = key.slice(prefixes.token.length);
    const candidate = readStoredProxyAuthCandidate(
      key,
      `${prefixes.expiry}${suffix}`,
    );
    if (candidate) {
      candidates.push(candidate);
    }
  }

  candidates.sort((a, b) => b.exp - a.exp);
  return candidates[0]?.token || "";
}

export function createStoredProxyAuthHeaders(
  options: StoredProxyAuthHeaderOptions = {},
): Record<string, string> {
  const token =
    options.chain !== undefined || options.wallet !== undefined
      ? readStoredEvmProxyAuthToken(
          options.chain ?? proxyAuthNetworkScope(),
          options.wallet,
        )
      : readAnyStoredProxyAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function storeProxyAuthToken(
  tokenKey: string,
  expKey: string,
  token: string,
  expiresAt?: number | null,
) {
  if (typeof window === "undefined") {
    return;
  }
  window.sessionStorage.setItem(tokenKey, token);
  if (
    typeof expiresAt === "number" &&
    Number.isFinite(expiresAt) &&
    expiresAt > 0
  ) {
    window.sessionStorage.setItem(expKey, String(expiresAt));
  } else {
    window.sessionStorage.removeItem(expKey);
  }
}

export async function tryRefreshProxyAuthToken(
  wallet: string,
): Promise<ProxyAuthTokenResponse | null> {
  if (!wallet) {
    return null;
  }
  try {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ wallet }),
    });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as ProxyAuthTokenResponse;
    return data?.token ? data : null;
  } catch {
    return null;
  }
}

export function revokeProxyAuthSession(wallet: string) {
  if (!wallet) {
    return;
  }
  void fetch("/api/auth/logout", {
    method: "POST",
    keepalive: true,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ wallet }),
  }).catch(() => {});
}
import { EVM_CONFIG } from "../config";
import type { EvmNetworkKey } from "../config/evmNetworks";
